import type { Deal } from "~/models/Deal";
import { getOriginVendor, isOriginVendor } from "~/models/DealVendorCompany";
import type {
	VendorCompany,
	VendorToCompanySetting
} from "~/models/VendorCompany";
import type { RelatedVendorPartner } from "~/models/VendorPartner";
import type { VendorUser } from "~/models/VendorUser";

export type VendorSession = {
	admin: boolean;
	/**
	 * Partner視点で、協力している一次請けの施工会社一覧
	 */
	relatedVendorPartners: RelatedVendorPartner[];
	vendorCompany: VendorCompany & VendorToCompanySetting;
	vendorUser: VendorSessionUser | null;
};

/**
 * Backendの新旧バージョンが混在する間は必須状態の項目が返らないことがあるため、省略可能にしている
 */
export type VendorSessionUser = VendorUser & {
	emailRegistrationRequired?: boolean;
	passwordChangeRequired?: boolean;
};

export type VendorUserProfileSetupRequirement = {
	emailRegistrationRequired: boolean;
	passwordChangeRequired: boolean;
};

export type VendorUserProfileSetupAlert = {
	description: string;
	restriction: string;
	title: string;
};

/**
 * ログイン中の担当者に必要なプロフィール設定（メールアドレスの登録・パスワードの設定・変更）を返す
 *
 * 管理者の代理ログインと担当者未ログインは対象外とする。
 * Backendから必須状態が返らない場合は、業務を止めないよう不要として扱う。
 * @param session
 */
export const getProfileSetupRequirement = (
	session: VendorSession
): VendorUserProfileSetupRequirement => {
	if (session.admin || session.vendorUser === null) {
		return { emailRegistrationRequired: false, passwordChangeRequired: false };
	}
	return {
		emailRegistrationRequired:
			session.vendorUser.emailRegistrationRequired ?? false,
		passwordChangeRequired: session.vendorUser.passwordChangeRequired ?? false
	};
};

/**
 * ログイン中の担当者が、通常業務の前にプロフィール設定を完了する必要があるかどうか
 * @param session
 */
export const isProfileSetupRequired = (session: VendorSession): boolean => {
	const { emailRegistrationRequired, passwordChangeRequired } =
		getProfileSetupRequirement(session);
	return emailRegistrationRequired || passwordChangeRequired;
};

/**
 * 必要なプロフィール設定に応じたアラートの文言を返す。設定が不要な場合はnullを返す
 * @param requirement
 */
export const buildProfileSetupAlert = ({
	emailRegistrationRequired,
	passwordChangeRequired
}: VendorUserProfileSetupRequirement): VendorUserProfileSetupAlert | null => {
	if (emailRegistrationRequired && passwordChangeRequired) {
		return {
			description:
				"セキュリティ強化のため、メールアドレスの登録とパスワードの設定・変更をお願いします。",
			restriction:
				"両方が完了するまで、案件の確認や操作などの機能は利用できません。",
			title: "メールアドレスの登録とパスワードの設定・変更が必要です"
		};
	}
	if (emailRegistrationRequired) {
		return {
			description:
				"セキュリティ強化のため、メールアドレスの登録をお願いします。",
			restriction:
				"登録が完了するまで、案件の確認や操作などの機能は利用できません。",
			title: "メールアドレスの登録が必要です"
		};
	}
	if (passwordChangeRequired) {
		return {
			description:
				"セキュリティ強化のため、パスワードの設定・変更をお願いします。",
			restriction:
				"設定・変更が完了するまで、案件の確認や操作などの機能は利用できません。",
			title: "パスワードの設定・変更が必要です"
		};
	}
	return null;
};

/**
 * 一次請けの施工会社に管理会社とのチャット許可を受けているかどうか
 * @param session
 * @param vendorCompanyId
 */
export const allowMessageByVendorCompany = (
	session: VendorSession,
	vendorCompanyId: number
): boolean =>
	session.relatedVendorPartners.some(
		(relatedVendorPartner) =>
			relatedVendorPartner.vendorCompany.id === vendorCompanyId &&
			relatedVendorPartner.allowMessage
	);

/**
 * ログインsessionから、dealに対してメッセージ可能かどうかを返す
 *
 * @param session
 * @param deal
 */
export const canMessage = (session: VendorSession, deal: Deal): boolean => {
	if (isOriginVendor(deal, session.vendorCompany.id)) {
		// originは常にメッセージ可能
		return true;
	}
	const originVendor = getOriginVendor(deal);
	if (originVendor === null) {
		return false;
	}
	return allowMessageByVendorCompany(session, originVendor.id);
};
